import type { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";

/** Descriptor for an in-progress card / action-button surface placement. */
export interface SurfacePlacementSession {
  readonly sceneId: string;
  readonly tileId: string;
  /** Action buttons say "button" in the instruction copy; text cards say "card". */
  readonly isButton: boolean;
}

/**
 * Web port of Unity's `GameObjectContextViewModel` surface-placement mode
 * (AI-787 / AI-922) for media tiles. Shared mode state between the card
 * inspector — which starts / confirms / cancels — and the runtime
 * `SurfacePlacementController`, which suppresses the camera and raycasts
 * each drag frame. Splitting it this way keeps the per-frame Three.js work
 * in the runtime layer while the composable stays DOM-only, mirroring how
 * the presenter laser tool is split.
 */
export interface ISurfacePlacementModel {
  readonly onChanged: Emitter<void>;
  readonly isActive: boolean;
  readonly session: SurfacePlacementSession | null;
  /** Viewport instruction copy, e.g. "Click anywhere on a surface\nto place your card". */
  readonly instructionText: string;
  /** Enter placement mode for a media tile (disables its billboard first). */
  beginForTileAsync(sceneId: string, tileId: string): Promise<void>;
  /** Live in-memory preview from the controller (Unity-space TRS). */
  preview(position: Vec3, rotationEuler: Vec3): void;
  /** Persist the previewed transform and exit. */
  commitAsync(): Promise<void>;
  /** Restore the initial transform and exit. */
  cancel(): void;
}

export const SURFACE_PLACEMENT_MODEL = Symbol.for("Campfire.SurfacePlacementModel");
